import * as React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import Plot from 'react-plotly.js';

/* -------------------- Helpers -------------------- */
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
};

/* -------------------- Component -------------------- */
export default function PkgUsageChart({ data, timeFilter, onTimeFilterChange }) {

const [openDialog, setOpenDialog] = React.useState(false);

  /* ---------------- Raw Records ---------------- */
  const usageRecords = React.useMemo(() => {
    if (!data?.usage_records) return [];
    return data.usage_records
      .map((r) => ({
        ...r,
        date: new Date(r.accessed_date),
        number_of_hits: Number(r.number_of_hits),
        user_name: r.user_name,
      }))
      .sort((a, b) => a.date - b.date);
  }, [data]);

  /* ---------------- Aggregate Data ---------------- */
  const buckets = React.useMemo(() => {
    if (!usageRecords.length) return [];

    const map = new Map();

    const getKey = (date) => {
      if (timeFilter === 'weekly') return getWeekStart(date).getTime();
      if (timeFilter === 'monthly') return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
      if (timeFilter === 'yearly') return new Date(date.getFullYear(), 0, 1).getTime();
      return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    };

    const getLabel = (date) => {
      if (timeFilter === 'weekly')
        return `Week of ${getWeekStart(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      if (timeFilter === 'monthly') return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (timeFilter === 'yearly') return date.getFullYear().toString();
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    usageRecords.forEach((r) => {
      const key = getKey(r.date);
      if (!map.has(key)) {
        map.set(key, {
          timestamp: key,
          label: getLabel(r.date),
          number_of_hits: 0,
          users: new Set(),
        });
      }
      map.get(key).number_of_hits += r.number_of_hits;
      map.get(key).users.add(r.user_name);
    });

    return Array.from(map.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [usageRecords, timeFilter]);

  /* ---------------- Limit visible periods ---------------- */
  const visibleBuckets = React.useMemo(() => {
    const lastN = { daily: 30, weekly: 12, monthly: 12, yearly: 5 };
    const N = lastN[timeFilter] || buckets.length;
    return buckets.slice(-N);
  }, [buckets, timeFilter]);

  /* ---------------- Selected Bucket ---------------- */
  const [selectedIndex, setSelectedIndex] = React.useState(visibleBuckets.length - 1);
  React.useEffect(() => {
    setSelectedIndex(visibleBuckets.length - 1);
  }, [visibleBuckets]);

  const selectedBucket = visibleBuckets[selectedIndex] || null;

  /* ---------------- Plot Data ---------------- */
  const plotData = [
    {
      x: visibleBuckets.map((b) => b.label),
      y: visibleBuckets.map((b) => b.number_of_hits),
      name: 'Usage Count',
      type: 'bar',
      marker: {
        color: 'rgba(40, 53, 126,0.8)',
        line: { color: 'rgba(40, 53, 126,1)', width: 1 },
      },
      hovertemplate: '%{x}<br>Usage: %{y}<extra></extra>',
    },
    {
      x: visibleBuckets.map((b) => b.label),
      y: visibleBuckets.map((b) => b.users.size),
      name: 'Unique Users',
      type: 'bar',
      marker: {
        color: 'rgba(46, 125, 50,0.8)',
        line: { color: 'rgba(46, 125, 50,1)', width: 1 },
      },
      hovertemplate: '%{x}<br>Unique Users: %{y}<extra></extra>',
    },
  ];

  /* ---------------- Layout ---------------- */
  const layout = {
    barmode: 'group',
    autosize: true,
    height: 420,
    margin: { l: 60, r: 20, t: 40, b: timeFilter === 'daily' ? 120 : 80 },
    legend: { orientation: 'h', x: 1, xanchor: 'right', y: 1.15, font: { size: 13 } },
    yaxis: { title: 'Count', fixedrange: false, tickfont: { size: 12 } },
    xaxis: { tickangle: -30, rangeslider: false, tickfont: { size: 12 } },
    hovermode: 'closest',
  };

  /* ---------------- Config ---------------- */
  const config = { responsive: true, scrollZoom: true, displaylogo: false, displayModeBar: true };

  /* ---------------- Render ---------------- */
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Toggle Buttons */}
      {/* Row with label at start and buttons centered */}
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', mb: 2 }}>
      {/* Label at start */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        Usage Over Time
      </Typography>

      {/* Toggle buttons centered */}
      <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
        <ToggleButtonGroup
          value={timeFilter}
          exclusive
          onChange={(e, v) => v && onTimeFilterChange(v)}
          size="small"
        >
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>


      {/* Graph + Card */}
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'flex-start' }}>
        {/* Graph 60% */}
        <Box
          sx={{
            width: '60%',
            minWidth: 600,
            height: 450,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            background: '#f8f9fa',
            padding: 1,
          }}
        >
          <Plot
            data={plotData}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%', borderRadius: 8 }}
            onClick={(e) => {
              if (!e.points?.length) return;
              const label = e.points[0].x;
              const idx = visibleBuckets.findIndex((b) => b.label === label);
              if (idx !== -1) setSelectedIndex(idx);
            }}
          />
        </Box>

        {/* Unique Users Card 40% */}
        {selectedBucket && (
          <Card
            sx={{
              width: '38%',
              height: 180, // fixed square-ish
              borderRadius: 3,
              boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%)',
              position: 'relative',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Unique Users
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5, color: '#2e7d32' }}>
                {selectedBucket.users.size}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedBucket.label}
              </Typography>

              {/* Dialog state */}
            {/* Icon to open Dialog */}
            <IconButton
            size="small"
            onClick={() => setOpenDialog(true)}
            sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                background: 'rgba(255,255,255,0.8)',
                '&:hover': { background: 'rgba(255,255,255,1)' },
            }}
            >
            <InfoOutlinedIcon fontSize="small" />
            </IconButton>

            {/* Dialog showing users */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Unique Users</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                {[...selectedBucket?.users || []].map((u) => (
                    <Typography key={u} variant="body2" sx={{ mb: 0.5 }}>
                    • {u}
                    </Typography>
                ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Close</Button>
            </DialogActions>
            </Dialog>

            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}
